import { Component, HostListener, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { Router } from '@angular/router'
import { User } from 'depoto-core/src/entities'
import { CoreService, LogoutService } from '../../services'
import { MultiSessionService, SessionEntry, SESSIONS_LS_KEY } from '../../services/multi-session.service'
import { ModalAddLoginComponent } from '../modal-components/modal-add-login/modal-add-login.component'
import { injectQueryClient } from '@tanstack/angular-query-experimental'
import buildInfo from '../../../environments/build-info.generated.json'
import { NotificationService } from '../../services/notification.service'
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'box-user',
  templateUrl: './box-user.component.html',
  styles: [
    `
      .dropdown-menu .dropdown-item:active {
        background-color: #f8f9fa;
        color: #16181b;
      }
    `,
  ],
  styleUrls: ['box-user.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoxUserComponent {
  @ViewChild('addLoginModal') addLoginModal: ModalAddLoginComponent

  private queryClient = injectQueryClient()

  appVersion = buildInfo.version
  commitShortHash = buildInfo.commitShortHash
  branchName = buildInfo.branchName
  commitDate = this.formatCommitDate(buildInfo.commitDate)

  user: User = new User({ email: '', avatar_url: 'assets/img/default-user.png' })
  sessions: SessionEntry[] = []
  activeSessionId: string | null = null
  isHidden: boolean

  constructor(
    private core: CoreService,
    private multiSession: MultiSessionService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private readonly logoutService: LogoutService,
    private readonly router: Router
  ) {
    window.getAppVersion = () => {
      return [
        `app:         ${this.appVersion}`,
        `commit:      ${buildInfo.commitHash}`,
        `branch:      ${this.branchName}`,
        `commit date: ${this.commitDate || 'unknown'}`,
      ]
    }
    this.core.services.user.currentUserEmitter.subscribe().then((user: User) => {
      this.user = user
      this.refreshSessions()
    })
  }

  private refreshSessions(): void {
    this.sessions = this.multiSession.getSessionsList()
    this.activeSessionId = this.multiSession.getActiveSessionId()
  }

  getOpenSessionUrl(sessionId: string): string {
    return this.multiSession.buildOpenSessionUrl(sessionId)
  }

  // Left-click switches the active account in this tab (no reload). Middle-click, ctrl/cmd/shift
  // -click and right-click's "open in new tab/window" should all fall through to the anchor's
  // href instead, opening a new tab/window logged in as that account.
  onSessionItemClick(event: MouseEvent, sessionId: string): void {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey) return
    event.preventDefault()
    this.switchTo(sessionId)
  }

  async switchTo(sessionId: string): Promise<void> {
    if (sessionId === this.activeSessionId) {
      this.isHidden = false
      return
    }
    try {
      await this.queryClient.cancelQueries({ type: 'all' } as any)
    } catch {}
    const result = await this.multiSession.switchToSession(sessionId)
    this.refreshSessions()
    this.isHidden = false
    if (result.ok) {
      // resetQueries() refreshes server data, but the components on the current route stay
      // mounted and keep the previous account's view state (filter selections, form drafts,
      // ...). Re-enter the *same* URL via a throwaway hop so those components are destroyed and
      // rebuilt from the new account's own persisted settings (BaseListing keys them per user),
      // without kicking the user off the page they were on. If the new account's route guards
      // reject that URL (missing permission), the guard redirects as it normally would.
      const currentUrl = this.router.url
      await this.router.navigateByUrl('/', { skipLocationChange: true })
      await this.router.navigateByUrl(currentUrl)
      await this.queryClient.resetQueries()
      return
    }
    if (result.reason === 'expired') {
      // An expired saved session is a heads-up, not a failure: a plain info toast
      // (same styling as the app's other notifications), plus the re-login modal
      // below which carries its own inline notice (isReauth). Session stays in the
      // list (see MultiSessionService.switchToSession) so the user can refresh its
      // token in place instead of losing it from their history.
      this.notificationService.info(this.translateService.instant('user.session-expired'))
      const entry = this.sessions.find(s => s.id === sessionId)
      if (entry) {
        this.addLoginModal.showChildModal({
          email: entry.email,
          clientType: entry.clientType,
          clientCustomUri: entry.clientCustomUri,
        })
      }
    } else {
      // Not a confirmed expiry (e.g. network error) - the token may still be fine, so don't force
      // the re-login modal, just let the user retry the switch.
      this.notificationService.error(this.translateService.instant('user.session-switch-failed'))
    }
  }

  openAddAccount(): void {
    this.isHidden = false
    this.addLoginModal.showChildModal()
  }

  async onAccountAdded(entry: SessionEntry): Promise<void> {
    await this.switchTo(entry.id)
  }

  removeSession(sessionId: string): void {
    this.multiSession.removeSession(sessionId)
    this.refreshSessions()
  }

  async logout(): Promise<void> {
    // Mirror LogoutService: move the URL off the current (possibly deep) route before
    // clearing auth, so logging back in lands on /home instead of re-rendering a stale
    // detail page. replaceUrl keeps that page out of history. Navigating while still
    // authenticated lets /home activate normally (the redirect-loop risk noted in
    // AppComponent is specific to navigating from the global onLogoutEmitter handler).
    if (this.router.url !== '/home') {
      await this.router.navigate(['/home'], { replaceUrl: true })
    }
    await this.multiSession.logoutCurrentSession({ forgetSignedInAccount: true })
  }

  // logout() {
  //   this.logoutService.logout()
  // }

  private formatCommitDate(value: string): string {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString()
  }

  @HostListener('window:storage', ['$event'])
  async onSessionsStorageChange(event: StorageEvent): Promise<void> {
    if (event.key !== SESSIONS_LS_KEY) return
    const action = this.multiSession.resolveCrossTabChange(event.newValue)
    if (action === undefined) {
      // Active session still exists — refresh the displayed list in case another account was added/removed
      this.refreshSessions()
      return
    }
    try {
      await this.queryClient.cancelQueries({ type: 'all' } as any)
    } catch {}
    // This tab's active session was removed in another tab (e.g. the user signed out there),
    // whether or not other remembered sessions still exist elsewhere. Mirror logoutCurrentSession()'s
    // policy of never silently picking another session for the user — always drop to the login
    // screen here too, which offers any remaining sessions as an explicit choice.
    await this.multiSession.logoutCurrentSession()
    this.refreshSessions()
    await this.queryClient.resetQueries()
  }

  @HostListener('window:mousedown', ['$event'])
  onUserBoxClick(event) {
    const isUserBox =
      (event.path || (event.composedPath && event.composedPath())).filter(
        d => d && typeof d.className?.includes === 'function' && d.className.includes('nav-settings')
      ).length > 0
    if (isUserBox) {
      this.isHidden = !this.isHidden
    } else {
      this.isHidden = false
    }
  }
}
